import "./KnxComObjectTable.scss";

import React, { useEffect, useRef, useState } from "react";
import { KnxMasterData } from "../KnxProductDataTools/LoadKnxMasterData";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import { belongsToBundle } from "../models/SignalBundles";
import { ChannelType, channelTypeInfo } from "../models/ChannelType";
import type Signal from "../models/Signal";
import Logger from "../Logger";
import { isUiModeDeveloper, isUiModeExpert, isUiModeProductAdmin } from "../uiMode";
import type Device from "../models/Device";
import ManualSignalTypeSelection from "./ManualSignalTypeSelection";
import { showToast } from "./Toasts";
import { itx, noItx } from "../translationItx/itx";

import type Channel from "../models/Channel";
import GroupAddress from "../models/GroupAdress";
import { signalBundleDefinitions } from "../models/SignalBundleDefinitions";

import ObjectReference from "./ObjectReference";
import { enumAvailableLanguages } from "../models/SettingsEnums";
import ConnectSignal, { moveSignalBackToIgnoreIfNoGroupAddresses } from "./ConnectSignal";
import Multiplexer from "../models/Multiplexer";
import Consumer from "../models/Consumer";
import BaseModel from "../models/BaseModel";
import SignalTypes from "../models/SignalTypes";
import ExpandButton from "./ExpandButton";
import { filterChannelsByUserRole } from "../models/Channel";
import { freezeTableColumnWidths } from "../modelViews/GraficElementUtils";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import { determineComObjectFunction } from "./KnxParameterViewer/ComObjectRefRef";

const logger = new Logger();

function isDraggable() {
  return isUiModeExpert() && (!isUiModeProductAdmin() || isUiModeDeveloper());
}

function OneGroupAddress(props: { groupAdress: GroupAddress; signal: Signal; requestChannel: Channel }) {
  const { groupAdress, requestChannel, signal } = props;

  useOnChangeWatcher(groupAdress);

  if (groupAdress.isDeleted) {
    return null;
  }

  let otherDevices: any = [];
  let otherDevicesFound = false;

  let thisSignalFound = false;

  const referencingSignals = groupAdress.getSignalBackpointers(true);
  referencingSignals.forEach((signal) => {
    if (signal.parent != requestChannel) {
      const channel = signal.parent as Channel;
      if (channel?.isChannel) {
        otherDevices += `<div>${channel.fullTag}&nbsp;${channel.parent?.name}</div> `;
      } else if (channel?.isButtonChannel) {
        otherDevices += `<div>${channel.fullTag}&nbsp;${channel.nameOrConnectedNames}</div> `;
      } else {
        logger.error("unexpected class", channel?.className);
        otherDevices += `<div>${signal.parent?.fullTag}&nbsp;${signal.parent?.name}</div> `;
      }
    } else {
      thisSignalFound = true;
    }
  });

  if (!thisSignalFound) {
    console.error("thisSignalFound is false", signal.name, signal.id);
  }

  if (otherDevices.length > 0) {
    otherDevicesFound = true;
    const s = itx(
      "knxCombObjTable.groupAdressConnectedWithDevices",
      "Diese Gruppenadresse ist mit folgenden anderen Geräte verbunden:",
    );
    otherDevices = `<div style={{ display: "flex", flexDirection: "row" }}>${s} ${otherDevices}</div>`;
  } else {
    const s = itx(
      "knxCombObjTable.groupAdressConnectedWithNoOtherDevice",
      "Diese Gruppenadresse ist mit KEINEM anderen Geräte verbunden",
    );
    otherDevices = `<div>${s}</div>`;
  }

  const blue = groupAdress.isManualGroupAddress;

  return (
    <ObjectReference
      obj={groupAdress}
      relative={signal}
      useAllwaysMaxSpace={false}
      showName={false}
      style={{
        backgroundColor: blue ? "lightblue" : "#eee",
        borderRadius: "20px",
      }}
      onUnlink={
        groupAdress.isManualGroupAddress
          ? () => {
              signal.requestSignal?.removeGroupAddress(groupAdress, true);
              groupAdress.raiseOnChange("childCollection");

              if (signal.parent?.parent) {
                moveSignalBackToIgnoreIfNoGroupAddresses(signal.parent.parent as Device, signal);
              }

              signal.requestSignal?.raiseOnChange("groupAddresses");
              signal.requestSignal?.raiseOnChange("childCollection");
              // open dialog to ask if group adress should be deletet if it dont have any signal backpointers left
              if (groupAdress.getSignalBackpointers().length == 0) {
                state.dialogManager.openDialog(
                  <ConfirmDialog
                    title={itx("KnxComObjectTable.emptyGroupAdress", "Gruppenadresse ohne Verwendungen")}
                    text={itx(
                      "KnxComObjectTable.deleteGroupAdress",
                      "Soll die nicht mehr verwendete Gruppenadresse gelöscht werden?",
                    )}
                    onOk={() => {
                      groupAdress.delete();
                      groupAdress.parent?.raiseOnChange("childCollection");
                      return true;
                    }}
                  />,
                );
              }
            }
          : undefined
      }
    />
  );
}

function SignalRow(props: {
  device: Device;
  signal: Signal;
  requestChannel: Channel | undefined;
  knxChannel: Channel | undefined;
  knxMasterData: KnxMasterData;
}) {
  const { device, requestChannel, knxChannel, signal, knxMasterData } = props;

  // Cache requestSignal so the getter (which inserts into the request channel map) is only
  // called once per row lifetime, not on every re-render.
  const cachedRequestSignal = React.useMemo(() => signal.requestSignal, []);
  useOnChangeWatcher(signal);
  useOnChangeWatcher(cachedRequestSignal);

  function explainationEmptyGroupAdresses(reqSignal: Signal) {
    let res: any = [];
    const connectedConsumer = requestChannel?.getConnectedConsumer();
    if (connectedConsumer?.changeChannelType) {
      const signalTypesFromConsumer = signalBundleDefinitions.get(connectedConsumer?.channelType);
      let inConsumerSignalBundle = false;
      signalTypesFromConsumer?.basicSignals
        ?.concat(signalTypesFromConsumer?.extendedSignals || [])
        .forEach((bundleSignal) => {
          if (bundleSignal == reqSignal.type) {
            inConsumerSignalBundle = true;
          }
        });

      inConsumerSignalBundle ||= signal.type.name == SignalTypes.Scene.name;

      if (!inConsumerSignalBundle) {
        res = (
          <div
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={itx(
              "knxComObjTable.explanationNoGroupAdress",
              "Dieser Sigaltype wird in diesem Kanaltype nicht verwendet",
            )}
            data-tooltip-place={"left"} // no itx
          >
            <i className="las la-info-circle" />
          </div>
        );
      }
    }
    return res;
  }

  const requirementSignal = requestChannel?.getSignalByNumber(signal.number);

  const elementsColumns: any = [];

  const signalBelongsToBundle = !knxChannel || belongsToBundle(knxChannel.channelType, signal);

  // const dpt = `${signal.knxDatapointType} ${
  //   knxMasterData.dpts.get(signal.knxDatapointType)?.Name
  // } `;

  // logger.log(`KnxObjectTable: ${signal.name}`, signal);
  let dpt = `${signal.knxDatapointType} ${knxMasterData.dpts.get(signal.knxDatapointType)?.Name || ""}`;

  let groupAddressesAreEmpty = false;
  let explain: any = null;
  if (requirementSignal && requirementSignal.groupAddresses.length == 0) {
    explain = explainationEmptyGroupAdresses(requirementSignal);
    groupAddressesAreEmpty = true;
  }

  elementsColumns.push(<td>{explain}</td>);

  elementsColumns.push(
    <td
      style={{
        color: signalBelongsToBundle ? "inherit" : "grey", // no itx
      }}
    >
      {signal.number}
    </td>,
  );

  let signalName: any = signal.name;

  if (isUiModeProductAdmin()) {
    signalName = signal.knxTextEN || signal.knxTextENForDetectionRaw || signal.knxTextENForDetection || "??";

    let signalNameLocal: string | undefined = undefined;
    if (state.settings.language != enumAvailableLanguages.english) {
      signalNameLocal = signal.name;
    }

    signalName = (
      <div style={{ marginBottom: "10px" }}>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <div className="rawBubble">{noItx("Display")}</div>
          <div>{signal.name}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <div className="rawBubble">{noItx("Match")}</div>
          <div>{signal.knxTextENForDetection}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <div className="rawBubble">{noItx("Raw")}</div>
          {signal.knxTextENForDetectionRaw}
        </div>
      </div>
    );
  }

  elementsColumns.push(
    <td style={{ userSelect: isUiModeProductAdmin() ? "text" : undefined }}>
      <div
        onDrop={(ev) => {
          signal.treeViewModel.onDrop(ev);
        }}
        draggable={isDraggable()}
      >
        {signalName}
      </div>
    </td>,
  );

  let knxFunctionText: any = signal.knxFunctionText;
  if (isUiModeProductAdmin()) {
    knxFunctionText = signal.knxFunctionTextEN;
    let signalFunctionLocal = "";

    if (state.settings.language != enumAvailableLanguages.english) {
      signalFunctionLocal = signal.knxFunctionText;
    }

    knxFunctionText = (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <div className="rawBubble">{noItx("Match")}</div>
          {knxFunctionText}
        </div>

        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <div className="rawBubble">{state.settings.language.split("-")[0]}</div>

          {signalFunctionLocal}
        </div>
      </div>
    );
  }

  elementsColumns.push(<td>{knxFunctionText}</td>);

  //
  // Gruppenadresse
  //

  const elmGroupAdresseses: any = [];

  requirementSignal?.groupAddresses.forEach((groupAdress) => {
    if (elmGroupAdresseses.length > 0) {
      elmGroupAdresseses.push(<div>&nbsp;</div>);
    }

    if (requestChannel) {
      elmGroupAdresseses.push(
        <OneGroupAddress signal={signal} groupAdress={groupAdress} requestChannel={requestChannel} />,
      );
    }
  });

  elementsColumns.push(
    <td>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          paddingLeft: "10px",
          alignItems: "center",
        }}
      >
        {isUiModeExpert() && <ConnectSignal device={device} signal={signal} />}
        {elmGroupAdresseses}
      </div>
    </td>,
  );

  //
  // Signaltype
  //

  if (isUiModeProductAdmin()) {
    elementsColumns.push(
      <td>
        <ManualSignalTypeSelection
          knxProductData={device.template?.knxProductData}
          signal={signal}
          isActor={device.isActor}
        />
      </td>,
    );
  } else {
    elementsColumns.push(<td>{itx("SignalTypes." + signal.type.name, signal.type.name)}</td>);
  }

  //
  // DPT
  //

  if (isUiModeExpert()) {
    if (dpt.toLowerCase().startsWith("dpst-")) {
      dpt = dpt.substring(5);
    }

    elementsColumns.push(<td>{dpt} </td>);
  }

  //
  // Developer Columns
  //

  if (isUiModeDeveloper()) {
    // elementsColumns.push(<td>{signal.knxNestedName}</td>);
    elementsColumns.push(
      <td>
        <div style={{ display: "flex", flexDirection: "column", fontSize: "0.7rem" }}>
          <div>{signal.knxId}</div>
          <div>{signal.knxChannelId}</div>
        </div>
      </td>,
    );
    elementsColumns.push(<td>{signal.determinedFromId}</td>);
  }

  // logger.log(`mwe1 Render signal ${signal.name} `);

  return (
    <tr
      key={signal.id}
      style={{
        color: signalBelongsToBundle ? "inherit" : "grey", // no itx
        userSelect: isUiModeProductAdmin() ? "text" : undefined,
      }}
      onDragEnter={(ev) => ev.preventDefault()}
      onDragOver={(ev) => ev.preventDefault()}
      onDragStart={(ev) => {
        signal.treeViewModel.dragStart(ev);
      }}
      draggable={isDraggable()}
      onDrop={(ev) => {
        signal.requestSignal?.treeViewModel.onDrop(ev);
      }}
    >
      {elementsColumns}
    </tr>
  );
}

function ChannelHeader(props: {
  device: Device;
  channel: Channel;
  requestChannel: Channel | undefined;
  channelTag: React.ReactNode;
  knxChannelName: string | undefined;
  isDeveloper: boolean;
  typeImage: React.ReactNode;
  xConsumers: any;
  channelType: ChannelType;
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
}) {
  const {
    device,
    channel,
    requestChannel,
    channelTag,
    knxChannelName,
    isDeveloper,
    typeImage,
    xConsumers,
    channelType,
    isExpanded,
    setIsExpanded,
  } = props;

  useOnChangeWatcher(channel);
  useOnChangeWatcher(requestChannel);

  return (
    <tr
      style={{
        backgroundColor: "var(--backgroundColorGrey)",
        color: "var(--white1)",
        cursor: "pointer",
      }}
      onClick={(ev) => {
        ev.stopPropagation();
        state.selectionManager.selectedObject = requestChannel;
      }}
      onDrop={(ev) => {
        requestChannel?.treeViewModel.onDrop(ev);
      }}
    >
      <td
        key={channel.id + " tag " + channelTag}
        colSpan={8}
        style={{ paddingLeft: "10px" }}
        onDragOver={(ev) => {
          ev.preventDefault();
        }}
        onDrop={(ev) => {
          requestChannel?.treeViewModel.onDrop(ev);
        }}
      >
        <div className="channelHeader estadaRow w100 left">
          <ExpandButton
            style={{ marginLeft: "-10px", color: "darkgrey" }}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
          />

          {isDeveloper && <span style={{ color: "black" }}>{channelTag} &nbsp;&nbsp;&nbsp; </span>}

          <div style={{ width: "20px" }}>
            {channel.channelType != ChannelType.Scene &&
              channel.channelType != ChannelType.Special &&
              !xConsumers &&
              channelTypeInfo(channel.channelType).canBeConnectedWithPartner == true && (
                <img
                  src="images/hint.png"
                  data-tooltip-id="tv-tooltip"
                  data-tooltip-variant="info" // no itx
                  data-tooltip-content={state.translate(
                    "knxCombObjTable.channelNotUsed",
                    "Dieser Kanal wird noch nicht verwendet",
                  )}
                  data-tooltip-place="right"
                  style={{ width: "20px", height: "20px" }}
                />
              )}
          </div>
          {typeImage}

          {knxChannelName}

          {device.isButton && (
            <div key={channel.id + " name " + requestChannel?.nameOrConnectedNames} style={{ paddingLeft: "20px" }}>
              {requestChannel?.name != knxChannelName && requestChannel?.name}
            </div>
          )}

          {xConsumers && (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", height: "30px" }}>
              {xConsumers}
            </div>
          )}

          <div style={{ flexGrow: 1 }}></div>

          <div key={channel.id + " channelype " + channel.channelType} style={{ paddingRight: "5px", color: "#aaa" }}>
            {state.translate(`channelType.${channel.channelType} `, channelType)}
          </div>

          {isUiModeDeveloper() && (
            <div
              key={channel.id + " channelDescription " + channel.channelType}
              style={{ paddingLeft: "20px", fontSize: "0.8em", color: "#ccc" }}
            >
              {channel.knxChannelDescriptionProperty && (
                <div>
                  {noItx("channelDescription=")}
                  {channel.knxChannelDescriptionProperty?.id} //
                  {channel.knxChannelDescriptionProperty?.instanceId}
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

async function onSaveMetadata(device: Device) {
  if (device.template) {
    const res = await device.template?.knxProductData.saveMetadataOnProductServer(device.template, device);
    if (res) {
      showToast({
        text: state.translate("tabGeneralTemplate.metadataSaved", "Metadata saved"),
      });
    } else {
      showToast({
        text: state.translate("tabGeneralTemplate.metadataNotSaved", "Metadata coudn`t be saved"),
      });
    }
  }
}

function OneChannel(props: {
  requestChannel: Channel | undefined;
  knxChannel: Channel | undefined;
  device: Device;
  knxMasterData: KnxMasterData;
}) {
  let { requestChannel, knxChannel, device, knxMasterData } = props;
  const mappedChannel = requestChannel?.mappedChannel || knxChannel?.mappedChannel;

  const [isExpanded, setIsExpanded] = useState(false);

  // Re-render when signals within this channel change their connected state (e.g. new group address assigned)
  // Must be called before any conditional logic to comply with Rules of Hooks
  useOnChangeWatcher(knxChannel?.signals);
  useOnChangeWatcher(knxChannel?.requestChannel?.signals);
  useOnChangeWatcher(requestChannel?.signals);
  useOnChangeWatcher(knxChannel, (changedProperty) => {
    if (changedProperty === "childCollection") {
      return true;
    }
    return false;
  });
  useOnChangeWatcher(mappedChannel, (changedProperty) => {
    if (changedProperty === "childCollection") {
      return true;
    }
    return false;
  });

  const elementsRows = new Array<any>();

  let channel: Channel;

  if (knxChannel) {
    knxChannel.assetIsKnxChannel();
    requestChannel = knxChannel.requestChannel;
    channel = knxChannel;
  } else {
    requestChannel?.assetIsRequestChannel();
    channel = requestChannel!;
  }

  let sortedSignals = (channel.treeViewModel.childCollection as Array<Signal>) || [];

  if (!isUiModeDeveloper()) {
    sortedSignals = sortedSignals.filter((signal) => signal.name.indexOf("DUMMY") == -1);
  }

  //
  // Filter "unused" signals
  //

  const usedSortedSignals = new Array<Signal>();

  sortedSignals.forEach((signal) => {
    const signalBelongsToBundle = belongsToBundle(channel.channelType, signal);
    const isConnectedWithGroupAddress = signal.groupAddresses.length > 0;

    if (isUiModeExpert() || isUiModeDeveloper() || signalBelongsToBundle || isConnectedWithGroupAddress) {
      usedSortedSignals.push(signal);
    }
  });

  if (usedSortedSignals.length == 0 && !isUiModeExpert()) {
    return null;
  }

  let consumers = new Array<Consumer | Multiplexer>();
  if (requestChannel) {
    if (device.isButton) {
      consumers = requestChannel.getConnectedConsumersOrMultiplexerforButton();
    } else {
      const c = requestChannel.getConnectedConsumer();
      if (c) {
        consumers = [c];
      } else {
        consumers = [];
      }
    }
  }

  const knxChannelName = knxChannel?.name || requestChannel?.name;

  const channelTag = requestChannel ? (
    `${requestChannel.tag || "--"} ${knxChannel?.tag || "--"} `
  ) : (
    <img
      src="images/alert.png"
      data-tooltip-id="tv-tooltip"
      data-tooltip-content={state.translate("knxCombObjTable.errorInChannelAssignment", "Fehler in der Kanalzuordnung")}
      data-tooltip-place="right"
      data-tooltip-variant="error"
      style={{ width: "25px", height: "25px" }}
    />
  );

  let xConsumers: any = [];
  consumers.forEach((consumer) => {
    xConsumers.push(
      <ObjectReference
        obj={consumer}
        relative={device.parent as BaseModel}
        onUnlink={() => {
          consumer.unlinkConnectionWith(requestChannel);
        }}
      />,
    );
  });

  if (xConsumers.length == 0) {
    xConsumers = undefined;
  }

  const isDeveloper = isUiModeDeveloper();

  let channelType = channel.channelType;
  if (!isUiModeDeveloper() && channelType == ChannelType.UnknownMapWithScript) {
    channelType = ChannelType.Unknown;
  }

  const url = channelTypeInfo(channel.channelType).image;
  let typeImage: any = null;

  if (url && url?.substring(0, 2) != "la") {
    typeImage = (
      <img
        className={`icon`}
        style={{
          width: "25px",
          height: "25px",
          objectFit: "contain",
          marginTop: "3px",
          marginRight: "10px",
          opacity: "1",
        }}
        src={url.replace(".png", "-white.png")} // no itx
      />
    );
  } else {
    typeImage = <div style={{ width: "20px", height: "20px", marginRight: "10px" }}></div>;
  }

  elementsRows.push(
    <ChannelHeader
      key={channel.id + " header"}
      device={device}
      channel={channel}
      requestChannel={requestChannel}
      channelTag={channelTag}
      knxChannelName={knxChannelName}
      isDeveloper={isDeveloper}
      typeImage={typeImage}
      xConsumers={xConsumers}
      channelType={channelType}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    />,
  );

  if (isExpanded) {
    usedSortedSignals.forEach((signal) => {
      elementsRows.push(
        <SignalRow
          device={device}
          signal={signal}
          requestChannel={requestChannel}
          knxChannel={knxChannel}
          knxMasterData={knxMasterData}
        />,
      );
    });

    if (elementsRows.length > 0) {
      //
      // no separator row on top
      //
      elementsRows.push(
        <tr
          style={{
            height: "20px",
            cursor: "pointer",
          }}
        >
          <td></td>
        </tr>,
      );
    }
  }

  return <>{elementsRows}</>;
}

export default function KnxComObjectTable(props: { device: Device }) {
  const { device } = props;
  const tableRef = useRef<HTMLTableElement>(null);
  const manualSignalTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(false);

  const knxChannels = device.knxChannels;
  const channels = device.channels;

  const [knxMasterData, setKnxMasterData] = useState<KnxMasterData | undefined>(undefined);

  useOnChangeWatcher(state.settings);
  // Watch both channel collections: knxChannels for KNX channel changes,
  // channels for when a new request channel (e.g. "Manuelle Signalverbindungen") is added
  useOnChangeWatcher(knxChannels);
  useOnChangeWatcher(channels);
  useOnChangeWatcher(device, (changedProperty) => {
    if (changedProperty === "manualSignalConnection") {
      if (manualSignalTimerRef.current) clearTimeout(manualSignalTimerRef.current);
      manualSignalTimerRef.current = setTimeout(() => {
        manualSignalTimerRef.current = undefined;
        if (!mountedRef.current) return;
        // because timedelay??
        initTab();
      }, 100);

      return true;
    }
    return false;
  });

  useEffect(() => {
    mountedRef.current = true;
    initTab();

    return () => {
      mountedRef.current = false;
      if (manualSignalTimerRef.current) {
        clearTimeout(manualSignalTimerRef.current);
        manualSignalTimerRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tableRef.current) {
        freezeTableColumnWidths(tableRef.current);
      }
    }, 200);

    return () => clearTimeout(timer);
  });

  function initTab() {
    state.loadKnxMasterData().then((knxMasterData) => {
      if (!mountedRef.current) return;
      setKnxMasterData(knxMasterData);
      device.removeUnusedChannels_WithoutKnxChannel_And_WithoutConnections();

      GroupAddress.updateAllBackpointers();

      device.localConsistencyCheck();

      device.channels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          if (signal.parent != channel) {
            console.error("signal.parent != channel (async check)", signal, channel); // no itx
            signal.parent = channel; // repair inconsistency
          }
        });
      });
    });
    GroupAddress.updateAllBackpointers();

    device.localConsistencyCheck();

    device.channels.forEach((channel) => {
      channel.signals.forEach((signal) => {
        if (signal.parent != channel) {
          console.error("signal.parent != channel (sync check)", signal, channel); // no itx
          signal.parent = channel; // repair inconsistency
        }
      });
    });
  }

  logger.log(`mwe1 Render KnxComObjectTable #channels: ${knxChannels.size} `);

  if (knxMasterData == undefined) {
    return null;
  }

  const elementsRows: any = [];
  const elementsHeader: any = [];

  elementsHeader.push(<th style={{ width: "10px" }}></th>);
  elementsHeader.push(<th>{state.translate("knxCombObjTable.number", "Nr")}</th>);
  elementsHeader.push(<th>{state.translate("name", "Name")}</th>);
  elementsHeader.push(<th>{state.translate("knxCombObjTable.function", "Function")}</th>);
  elementsHeader.push(<th>{state.translate("knxCombObjTable.GrpAdr", "GrpAdr")}</th>);
  elementsHeader.push(<th>{state.translate("knxCombObjTable.type", "Type")}</th>);

  if (isUiModeExpert() || isUiModeDeveloper()) {
    elementsHeader.push(<th>{state.translate("knxCombObjTable.dpt", "DPT")}</th>);
  }

  if (isUiModeDeveloper()) {
    // elementsHeader.push(<th>{itx("KnxComObjectTable.nestedname", "NestedName")}</th>);
    elementsHeader.push(<th>{itx("KnxComObjectTable.knxid", "KnxId")}</th>);
    elementsHeader.push(<th>{noItx("determineID")}</th>);
  }

  elementsHeader.push(<th style={{ width: "100%" }}></th>);

  let sortedKnxChannels = knxChannels.sortIntBy("name");

  const specialTypes = [
    ChannelType.Environment,
    ChannelType.General,
    ChannelType.Unknown,
    ChannelType.UnknownMapWithScript,
    ChannelType.Special,
    ChannelType.ManualSignals,
    ChannelType.Ignored,
  ];

  let specialChannels = sortedKnxChannels.filter((knxChannel) => specialTypes.includes(knxChannel.channelType));
  specialChannels = specialChannels.filter(
    (knxChannel) => knxChannel.signals.size > 0 || knxChannel.requestChannel != undefined || isUiModeDeveloper(),
  );

  sortedKnxChannels = sortedKnxChannels.filter((knxChannel) => !specialTypes.includes(knxChannel.channelType));
  sortedKnxChannels = sortedKnxChannels.concat(specialChannels);

  sortedKnxChannels = filterChannelsByUserRole(device, sortedKnxChannels);

  const channelsWithoutKnxChannel = device.channels.findAsArray((channel) => !channel.knxChannel);

  return (
    <div>
      {isUiModeDeveloper() && (
        <div style={{ marginBottom: "20px", padding: "10px", width: "100%", backgroundColor: "yellow" }}>
          Debug: RuleId: &nbsp;{/* no itx */}
          <input
            type="text"
            defaultValue={state.debugData?.signalRuleIdToAnalyse || ""}
            key={state.debugData?.signalRuleIdToAnalyse || ""}
            style={{ width: "100px" }}
            onChange={(ev: any) => {
              state.debugData = { ...state.debugData, signalRuleIdToAnalyse: ev.target.value };
            }}
          />
          &nbsp; SignalNumber: &nbsp;{/* no itx */}
          <input
            type="text"
            defaultValue={state.debugData?.signalNumberToAnalyse || ""}
            key={state.debugData?.signalNumberToAnalyse || ""}
            onChange={(ev: any) => {
              state.debugData = { ...state.debugData, signalNumberToAnalyse: ev.target.value };
            }}
          />
          &nbsp;
          <button
            className="button"
            onClick={() => {
              device.allKnxSignals().forEach((signal) => {
                const res = determineComObjectFunction(device, device.template!.knxProductData, device.isActor, signal);
              });
            }}
          >
            {itx("KnxComObjectTable.test", "Test")}
          </button>
        </div>
      )}

      <table
        ref={tableRef}
        style={{
          minWidth: "100%",
        }}
      >
        <thead>{elementsHeader}</thead>
        <tbody>
          {sortedKnxChannels.map((knxChannel) => (
            <OneChannel
              key={knxChannel.id}
              knxChannel={knxChannel}
              requestChannel={undefined}
              device={device}
              knxMasterData={knxMasterData!}
            />
          ))}

          {channelsWithoutKnxChannel.length > 0 && (
            <tr
              style={{
                height: "20px",
                cursor: "pointer",
              }}
            >
              <td colSpan={8} style={{ padding: "0px", paddingTop: "50px" }}>
                <div
                  style={{
                    width: "100%",
                    margin: "0px",
                    backgroundColor: "var(--backgroundColorError",
                    color: "var(----fontColorError",
                    padding: "5px",
                  }}
                >
                  {state.translate(
                    "knxCombObjTable.noRealisationInDevice",
                    "Für die folgenden Funktionen gibt keine Realisierungen in diesem Gerät.",
                  )}
                  <br />
                  {state.translate(
                    "knxCombObjTable.instructionHelp",
                    "Bitte änderen Sie die Parameter, oder verschieben Sie diese Funktionen zu einem anderen Gerät",
                  )}
                </div>
              </td>
            </tr>
          )}

          {channelsWithoutKnxChannel.map((requestChannel) => (
            <OneChannel
              key={requestChannel.id}
              requestChannel={requestChannel}
              knxChannel={undefined}
              device={device}
              knxMasterData={knxMasterData!}
            />
          ))}
        </tbody>
      </table>

      {isUiModeProductAdmin() && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            className="button"
            style={{ marginTop: "20px" }}
            onClick={() => {
              onSaveMetadata(device);
            }}
          >
            {itx("knxCombObjTable.saveMetadata", "Save Metadata on Productserver")}
          </button>
        </div>
      )}
    </div>
  );
}
